<template>
	<BaseLayout
		:footer-full="true"
		:include-padding="false"
		:footnotes="footnotes"
		:background-color-class="'bg-color-white'"
	>
		<div class="marketing-site-about">
			<MarketingPageHeader :header="pageHeader" />
			<div class="image-overlap-section mt-200">
				<div class="container-lg">
					<div class="row">
						<div class="col-12 col-10-md col-offset-1-md">
							<video
								id="office-video"
								class="office-video"
								muted
								autoplay="true"
								crossorigin="Anonymous"
								loop
								role="presentation"
								webkit-playsinline="true"
								playsinline="true"
								disableRemotePlayback="true"
								:poster="appCloudfrontPath + '/images/marketing-site/CareersVideoPlaceholder.jpg'"
								preload="auto"
							>
								<source :src="appCloudfrontPath + '/video/Fundrise_careers.mp4'" type="video/mp4" />
							</video>
						</div>
					</div>
				</div>
			</div>
			<article class="bg-color-greige marketing-section pb-200">
				<div class="container pt-600">
					<div class="grid">
						<div class="col-8-md col-offset-2-md">
							<h3 class="heading-5 mb-100">We set out to build a better way to invest</h3>
							<p class="delta-md">
								Real estate has historically been one of the best performing investment assets and a
								core part of many of the most successful investor’s portfolios.
								<sup>1</sup>
							</p>
							<p class="delta-md">
								But investing in real estate (the way institutions do) traditionally hasn’t been
								available to most investors without either meeting high net worth requirements or going
								through a complex and inefficient process, riddled with high fees and outdated
								middlemen.
								<sup>2</sup>
							</p>
							<p class="delta-md">
								We started Fundrise with a simple idea: to use technology to make high-quality real
								estate investments available to everyone at a low cost.
							</p>
							<p class="delta-md">
								In the beginning, we faced our fair share of skeptics, especially within the
								institutional investment industry. They said our idea wasn’t possible. Turns out, they
								were wrong.
							</p>
							<p class="delta-md">
								Since launching our first offering in 2012, we’ve invested in more than
								{{ assetValue }} worth of real estate across the country. Today, we manage more than
								{{ netAUM }} of equity on behalf of more than {{ investorCount }} individual investors.
							</p>
							<blockquote
								class="blockquote gamma bg-color-white-smo px-150 pt-150 pb-150-smo mt-150 mb-200 mt-200-lg"
							>
								<p>
									Our goal is to build a better financial system for the individual investor &mdash;
									one that is simpler, more reliable, lower cost and transparent.
								</p>

								<cite class="blockquote-attribution mt-100">
									<figure class="team-member team-member-background--dots">
										<img
											class="team-member__image"
											alt="Ben Miller, Co-Founder &amp; CEO"
											:src="appCloudfrontPath + '/interface/team-member/staff-benmiller.png'"
										/>
									</figure>
									<figcaption class="team-member__caption zeta ml-100">
										<span class="team-member__name font-weight-bold text-transform-uppercase"
											>Ben Miller</span
										>
										Co-founder &amp; CEO <br />
										<BaseLink :link="benTwitterLink">
											<BaseSvgIcon :name="benTwitterLink.text" height="14" />
										</BaseLink>
									</figcaption>
								</cite>
							</blockquote>
							<p class="delta-md">
								Although our business and the technology we build has continued to evolve, our mission
								has always remained the same: to empower the individual investor. That’s why we obsess
								over things like cost efficiency, minimizing fees, frequent candid communication, and
								developing smarter tools that provide better insights.
							</p>
							<p class="delta-md">
								Perhaps the clearest manifestation of this investor-first approach is the fact that
								we’re investor-owned, allowing investors in Fundrise real estate portfolios to become
								fellow shareholders of the company itself through our unique iPO model. This lets us
								minimize our dependency on outside capital and better align our long-term interests with
								the long-term interests of our investors — in short, making our success their success.
							</p>
							<p class="delta-md pb-600">
								Almost a decade later, we’re still just getting started. Welcome to the future of real
								estate investing.
							</p>
						</div>
					</div>
				</div>
			</article>
			<CurrentStats />
			<section class="marketing-section section--about-join-us bg-color-content-primary pt-600 pb-600">
				<div class="container text-align-center pt-300 pt-400-lg pb-300-lg">
					<div class="grid-md gutters-300-md text-color-white">
						<div class="col-6-md col-offset-3-md">
							<h2 class="heading-1 text-color-white">Come work with us</h2>
							<p class="delta text-align-left">
								Fundrise is headquartered in the Dupont Circle area of Washington, DC. We’ve been
								recognized as one of the Washington Post’s Top Workplaces, and named to the Forbes
								Fintech 50 three times.
							</p>
							<p class="delta text-align-left">
								We look for low-ego, results-driven people with a penchant for bold ideas and an
								obsession with solving problems for our investors.
							</p>

							<BaseLink :link="careersLink" classes="mt-300 button button-primary" />
						</div>
					</div>
				</div>
			</section>
		</div>
	</BaseLayout>
</template>

<script setup lang="ts">
import { LandingPageHeader, Link } from 'types/layout';
import { appCloudfrontPath } from '@constants';
import CurrentStats from '@components/marketing-site/current-stats.vue';
import { getSocialStats } from '@api/sanity/graphql/global-social-stats';
import MarketingPageHeader from '@components/marketing-site/marketing-page-header.vue';
import { ref } from 'vue';
import { SanityGlobalSocialStats } from 'types/sanity/marketing-components';
import { useHead } from '@unhead/vue';

const assetValue = ref('');
const investorCount = ref('');
const netAUM = ref('');

const pageHeader: LandingPageHeader = {
	title: 'About Us',
	heading: "We're on a mission to build a better financial system by empowering the individual.",
	summary: [
		`Founded in 2012 and headquartered in Washington, DC, Fundrise is one of the leading real estate investment platforms.`
	]
};

const careersLink: Link = {
	text: 'Learn more',
	router: 'careers'
};

const benTwitterLink = {
	text: 'twitter',
	href: 'https://twitter.com/BenMillerise',
	target: '_blank'
};

const footnotes: Array<string> = [
	'Blackstone <a href="https://www.blackstone.com/docs/default-source/black-papers/seeking-an-alternative_standard.pdf" target="_blank" class="text-color-current-color">"Seeking an Alternative: Understanding and Allocating to Alternative Investments"</a>. November 2018.',
	'Statement about high fees is based on a comparison of typical fees charged for a Fundrise standard portfolio offering to the benchmark average commitment fee and mode asset management fee reported by the Preqin 2019 Private Equity Real Estate Market Outlook. Funds reviewed by the Market Outlook may have other material differences from the investment vehicles offered by Fundrise, and may not be representative of private equity funds or commercial real estate investment opportunities.'
];

async function setup(): Promise<void> {
	const metaTitle = 'About Us';
	const metaDescription =
		'Fundrise gives all U.S. investors access to high quality private market investing opportunities through our simple, transparent and efficient platform.';

	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription }
		]
	});
	const socialStats: SanityGlobalSocialStats | null = await getSocialStats('about-view');
	const investorStats = socialStats?.statistics.find((stat) => stat.slug.current === 'investors');
	investorCount.value = investorStats?.value || '387,000+';
	const assetValueStats = socialStats?.statistics.find((stat) => stat.slug.current === 'assetValue');
	assetValue.value = assetValueStats?.longFormStatistic || '$7 billion';

	const netAUMStats = socialStats?.statistics?.find((stat) => stat.slug.current === 'netAUM');
	netAUM.value = netAUMStats?.longFormStatistic || '$3 billion';
}
setup();
</script>

<style lang="scss">
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;

.marketing-site-about {
	@include respond-to(lg) {
		padding-top: 3rem;
	}

	video {
		width: 100%;
	}

	.current-stats-section {
		margin-bottom: -6rem;

		&:after {
			height: 85%;
			top: 0;

			@include respond-to(md) {
				height: 75%;
			}
		}
	}

	.image-overlap-section {
		z-index: 1;
		background: transparent;
		margin-bottom: -6rem;
		position: relative;
		&:after {
			content: '';
			width: 100%;
			height: 75%;
			background: white;
			position: absolute;
			top: 0;
			z-index: -1;
		}
	}

	.team-filter {
		margin: 0 auto 2rem;
		max-width: 210px;
	}
}

.marketing-section.section--about-join-us {
	background-image: url($cloud-front-domain + '/images/marketing-site/about_joinus.png');
	background-position: top center;
	background-repeat: no-repeat;
	background-size: cover;
}

.team-member {
	background-color: token('bg-secondary');
	background-repeat: repeat;
	background-size: auto;
	border-radius: 50%;
	display: inline-block;
	height: 60px;
	margin: 0;
	max-width: 100%;
	overflow: hidden;
	vertical-align: middle;
	width: 60px;

	@include respond-to(lg) {
		height: 80px;
		width: 80px;
	}

	&.team-member-background--dots {
		background-image: url($cloud-front-domain + '/images/marketing-site/dots.png');
	}

	&.team-member-background--lines {
		background-image: url($cloud-front-domain + '/images/marketing-site/lines.png');
	}

	&.team-member-background--crosshatch {
		background-image: url($cloud-front-domain + '/images/marketing-site/crosshatch.png');
	}

	+ .team-member__caption {
		display: inline-block;
		vertical-align: middle;

		.team-member__name {
			display: block;
			letter-spacing: 1.5px;
		}

		.svg-icon {
			color: token('content-tertiary');

			&:hover,
			&:focus {
				color: token('brand-secondary');
			}
		}
	}
}
</style>
